/**
 * Config for every role dashboard. Each dashboard renders a
 * DashboardTemplate with these props.
 *
 * Keys must match the backend `role` field.
 */

export const DASHBOARDS = {
    ceo: {
        title: "CEO Command Center",
        eyebrow: "Command",
        purpose:
            "Company-wide business intelligence — decisions, money, department pulse, and AI agent activity, all in one place.",
        actions: [
            { label: "Open CEO Chat", primary: true, locked: true },
            { label: "View Money Meter", locked: true },
            { label: "Review Decisions" },
            { label: "Scan Business File", locked: true },
        ],
        cards: [
            { label: "Today’s Revenue", value: "₹4.8L", hint: "+18% vs yesterday", tone: "positive" },
            { label: "Cash Available", value: "₹18.4L", hint: "Runway 4.5 mo" },
            { label: "Pending Approvals", value: "6", hint: "Across 4 departments" },
            { label: "Open Risks", value: "2", hint: "1 legal · 1 procurement", tone: "warning" },
            { label: "Team Updates", value: "14", hint: "New in last 24h" },
            { label: "Decision Cards", value: "31", hint: "Generated this week" },
        ],
        sections: [
            { title: "Company Health", body: "Live pulse across finance, sales, ops, and people." },
            { title: "Pending Decision Cards", body: "Actionable cards awaiting approve / simulate / reject." },
            { title: "Money Meter Placeholder", body: "Realtime money health with future-impact simulation.", locked: true },
            { title: "Department Updates", body: "Latest submissions from every department." },
            { title: "AI Agent Activity", body: "What the 10 agents are analyzing right now.", locked: true },
            { title: "Recent Approvals", body: "Every decision you approved this week." },
            { title: "CEO Chat Placeholder", body: "Ask questions in natural language — coming soon.", locked: true },
        ],
    },
    sales: {
        title: "Sales Workspace",
        eyebrow: "Sales",
        purpose:
            "Log leads, deals, and pipeline updates. Everything you submit becomes a signal for the CEO.",
        actions: [
            { label: "Add Lead", primary: true },
            { label: "Update Deal" },
            { label: "Submit Sales Report" },
        ],
        cards: [
            { label: "Total Leads", value: "184", hint: "+12 this week" },
            { label: "Hot Leads", value: "27", hint: "Won-probability > 60%" },
            { label: "Pipeline Value", value: "₹92L", hint: "Across 41 deals" },
            { label: "Closed Revenue", value: "₹18.6L", hint: "This month" },
            { label: "Follow-ups Due", value: "9", hint: "Today", tone: "warning" },
        ],
        sections: [
            { title: "Lead Entry" },
            { title: "Deal Pipeline" },
            { title: "Follow-ups" },
            { title: "Closed Deals" },
            { title: "Lost Deals" },
            { title: "Sales Notes" },
        ],
    },
    finance: {
        title: "Finance Workspace",
        eyebrow: "Finance",
        purpose:
            "Submit expenses, invoices, revenue, and cash updates — feeding the CEO Money Meter.",
        actions: [
            { label: "Add Expense", primary: true },
            { label: "Upload Invoice" },
            { label: "Submit Finance Report" },
        ],
        cards: [
            { label: "Cash Balance", value: "₹18.4L", hint: "As of today" },
            { label: "Monthly Revenue", value: "₹42L", hint: "MTD" },
            { label: "Monthly Expenses", value: "₹27.3L", hint: "MTD" },
            { label: "Pending Invoices", value: "12", hint: "₹6.8L outstanding" },
            { label: "Runway", value: "4.5 mo", hint: "Placeholder", locked: true },
        ],
        sections: [
            { title: "Expense Entry" },
            { title: "Invoice Upload" },
            { title: "Revenue Update" },
            { title: "Vendor Payments" },
            { title: "Cash Balance" },
            { title: "Pending Dues" },
        ],
    },
    hr: {
        title: "HR Workspace",
        eyebrow: "People",
        purpose:
            "Track hiring, attendance, salary, performance, and leave — the people signal for the CEO.",
        actions: [
            { label: "Add Hiring Request", primary: true },
            { label: "Submit Attendance" },
            { label: "Update HR Report" },
        ],
        cards: [
            { label: "Open Roles", value: "7", hint: "Across 3 teams" },
            { label: "Employees Active", value: "42", hint: "Techorithm Labs" },
            { label: "Leave Requests", value: "4", hint: "Pending your review" },
            { label: "Hiring Priority", value: "P1", hint: "Senior engineer", tone: "warning" },
            { label: "Team Capacity", value: "88%", hint: "Healthy" },
        ],
        sections: [
            { title: "Hiring Needs" },
            { title: "Attendance" },
            { title: "Salary Updates" },
            { title: "Performance Notes" },
            { title: "Leave Requests" },
            { title: "Open Positions" },
        ],
    },
    operations: {
        title: "Operations Workspace",
        eyebrow: "Ops",
        purpose:
            "Daily tasks, delays, vendor coordination and inventory — the delivery heartbeat.",
        actions: [
            { label: "Add Task", primary: true },
            { label: "Report Delay" },
            { label: "Submit Operations Update" },
        ],
        cards: [
            { label: "Tasks Active", value: "38" },
            { label: "Delayed Tasks", value: "5", tone: "warning" },
            { label: "Inventory Alerts", value: "2" },
            { label: "Operational Risks", value: "1", tone: "warning" },
            { label: "Pending Vendor Actions", value: "7" },
        ],
        sections: [
            { title: "Daily Tasks" },
            { title: "Inventory Status" },
            { title: "Project Delays" },
            { title: "Resource Issues" },
            { title: "Vendor Coordination" },
            { title: "Delivery Status" },
        ],
    },
    marketing: {
        title: "Marketing Workspace",
        eyebrow: "Growth",
        purpose:
            "Campaigns, budgets, content, and ROI — the growth signal the CEO sees.",
        actions: [
            { label: "Add Campaign", primary: true },
            { label: "Update Ad Spend" },
            { label: "Submit Marketing Report" },
        ],
        cards: [
            { label: "Campaigns Active", value: "6" },
            { label: "Ad Spend", value: "₹3.4L", hint: "This month" },
            { label: "Leads Generated", value: "412" },
            { label: "ROI", value: "3.1x", hint: "Placeholder", locked: true },
            { label: "Content Pending", value: "8" },
        ],
        sections: [
            { title: "Campaigns" },
            { title: "Ad Budget" },
            { title: "Leads Generated" },
            { title: "Content Calendar" },
            { title: "Influencer Activity" },
            { title: "ROI Tracking", locked: true },
        ],
    },
    customer: {
        title: "Customer Support Workspace",
        eyebrow: "Support",
        purpose:
            "Tickets, complaints, refunds, escalations and feedback — the customer signal.",
        actions: [
            { label: "Add Ticket", primary: true },
            { label: "Escalate Issue" },
            { label: "Submit Support Report" },
        ],
        cards: [
            { label: "Open Tickets", value: "23" },
            { label: "Complaints Today", value: "4", tone: "warning" },
            { label: "Escalations", value: "2", tone: "warning" },
            { label: "Refund Requests", value: "3" },
            { label: "Customer Risk", value: "Low" },
        ],
        sections: [
            { title: "Tickets" },
            { title: "Complaints" },
            { title: "Refund Requests" },
            { title: "Escalations" },
            { title: "Customer Feedback" },
            { title: "Response Time" },
        ],
    },
    procurement: {
        title: "Procurement Workspace",
        eyebrow: "Procurement",
        purpose:
            "Vendors, purchase requests, quotations and stock — the cost signal.",
        actions: [
            { label: "Create Purchase Request", primary: true },
            { label: "Add Vendor Quote" },
            { label: "Submit Procurement Report" },
        ],
        cards: [
            { label: "Pending Purchases", value: "9" },
            { label: "Vendor Quotes", value: "14" },
            { label: "Stock Alerts", value: "3", tone: "warning" },
            { label: "Invoices Pending", value: "6" },
            { label: "Cost Saving", value: "₹1.4L", hint: "Placeholder", locked: true },
        ],
        sections: [
            { title: "Purchase Requests" },
            { title: "Vendor Details" },
            { title: "Quotations" },
            { title: "Stock Requirements" },
            { title: "Invoice Uploads" },
            { title: "Delivery Timelines" },
        ],
    },
    legal: {
        title: "Legal Workspace",
        eyebrow: "Legal",
        purpose:
            "Contracts, compliance, renewals, risks and policies — the risk signal.",
        actions: [
            { label: "Upload Contract", primary: true },
            { label: "Add Legal Note" },
            { label: "Submit Legal Report" },
        ],
        cards: [
            { label: "Contracts Active", value: "31" },
            { label: "Renewals Due", value: "4", tone: "warning" },
            { label: "Compliance Alerts", value: "1", tone: "warning" },
            { label: "Legal Risks", value: "2" },
            { label: "Pending Reviews", value: "5" },
        ],
        sections: [
            { title: "Contracts" },
            { title: "Compliance Notes" },
            { title: "Legal Risks" },
            { title: "Agreement Renewals" },
            { title: "Policy Documents" },
            { title: "Pending Approvals" },
        ],
    },
    strategy: {
        title: "Strategy Workspace",
        eyebrow: "Strategy",
        purpose:
            "Goals, market research, competitor and expansion notes — the horizon signal.",
        actions: [
            { label: "Add Strategy Note", primary: true },
            { label: "Submit Market Research" },
            { label: "Create Strategy Report" },
        ],
        cards: [
            { label: "Growth Ideas", value: "12" },
            { label: "Competitor Updates", value: "5" },
            { label: "Expansion Options", value: "3" },
            { label: "Strategic Risks", value: "2", tone: "warning" },
            { label: "CEO Briefings", value: "1", hint: "This week" },
        ],
        sections: [
            { title: "Business Goals" },
            { title: "Competitor Notes" },
            { title: "Expansion Ideas" },
            { title: "Partnership Ideas" },
            { title: "Market Research" },
            { title: "Quarterly Plans" },
        ],
    },
    meeting: {
        title: "Meeting Admin Workspace",
        eyebrow: "Meetings",
        purpose:
            "Meeting notes, agendas, action items and follow-ups — the memory of the company.",
        actions: [
            { label: "Create Meeting Note", primary: true },
            { label: "Add Action Item" },
            { label: "Submit Meeting Summary" },
        ],
        cards: [
            { label: "Meetings Today", value: "5" },
            { label: "Pending Action Items", value: "17", tone: "warning" },
            { label: "Decisions Logged", value: "42" },
            { label: "Follow-ups Due", value: "9" },
            { label: "CEO Briefings", value: "3" },
        ],
        sections: [
            { title: "Meeting Notes" },
            { title: "Agenda" },
            { title: "Action Items" },
            { title: "Attendees" },
            { title: "Decisions Taken" },
            { title: "Follow-up Tasks" },
        ],
    },
};

export const ROLE_LABEL = {
    ceo: "CEO",
    sales: "Sales",
    finance: "Finance",
    hr: "HR",
    operations: "Operations",
    marketing: "Marketing",
    customer: "Customer Support",
    procurement: "Procurement",
    legal: "Legal",
    strategy: "Strategy",
    meeting: "Meeting Admin",
    company_admin: "Company Admin",
    platform_admin: "Platform Admin",
};
